import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch, Globe, Lock, Table2, Trash2, X } from 'lucide-react';
import { Link } from 'react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useWorkArea } from '@/contexts/WorkAreaContext';
import {
  addWorkAreaCodebase,
  deleteWorkArea,
  listCodebases,
  listWorkAreaCodebases,
  patchWorkArea,
  removeWorkAreaCodebase,
} from '@/lib/api';
import type { CodebaseResponse, PatchWorkAreaBody, WorkArea, WorkAreaVisibility } from '@/lib/api';
import { WorkAreaEnvVarsPanel } from './WorkAreaEnvVarsPanel';

function formatDate(value: string): string {
  return value.slice(0, 19).replace('T', ' ');
}

function codebaseLabel(codebase: CodebaseResponse): string {
  return codebase.github_repo_full_name ?? codebase.name;
}

interface MetadataRowProps {
  label: string;
  value: React.ReactNode;
}

function MetadataRow({ label, value }: MetadataRowProps): React.ReactElement {
  return (
    <div>
      <div className="font-mono text-[9.5px] font-bold uppercase tracking-widest text-text-tertiary">
        {label}
      </div>
      <div className="mt-1 break-words rounded-md border border-border bg-surface-inset px-2.5 py-1.5 text-xs text-text-primary">
        {value}
      </div>
    </div>
  );
}

export function WorkAreaDetail(): React.ReactElement {
  const queryClient = useQueryClient();
  const { selectedWorkArea, setSelectedWorkAreaId } = useWorkArea();
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<WorkAreaVisibility>('team');
  const [codebaseToAdd, setCodebaseToAdd] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const workAreaId = selectedWorkArea?.id ?? null;

  const { data: codebases } = useQuery({
    queryKey: ['codebases'],
    queryFn: listCodebases,
  });

  const {
    data: memberships,
    isLoading: loadingMemberships,
    isError: membershipsError,
  } = useQuery({
    queryKey: ['workAreaCodebases', workAreaId],
    queryFn: () => listWorkAreaCodebases(workAreaId ?? ''),
    enabled: workAreaId !== null,
  });

  useEffect(() => {
    if (!selectedWorkArea) return;
    setName(selectedWorkArea.name);
    setVisibility(selectedWorkArea.visibility);
    setMessage(null);
  }, [selectedWorkArea]);

  const codebaseById = useMemo(() => {
    const map = new Map<string, CodebaseResponse>();
    for (const codebase of codebases ?? []) {
      map.set(codebase.id, codebase);
    }
    return map;
  }, [codebases]);

  const memberCodebaseIds = useMemo(
    () => new Set((memberships?.codebases ?? []).map(membership => membership.codebaseId)),
    [memberships]
  );

  const memberCodebaseIdList = useMemo(
    () => (memberships?.codebases ?? []).map(membership => membership.codebaseId),
    [memberships]
  );

  const addableCodebases = useMemo(
    () =>
      [...(codebases ?? [])]
        .filter(codebase => !memberCodebaseIds.has(codebase.id))
        .sort((a, b) => codebaseLabel(a).localeCompare(codebaseLabel(b))),
    [codebases, memberCodebaseIds]
  );

  useEffect(() => {
    if (codebaseToAdd && !addableCodebases.some(codebase => codebase.id === codebaseToAdd)) {
      setCodebaseToAdd('');
    }
  }, [addableCodebases, codebaseToAdd]);

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchWorkAreaBody }) => patchWorkArea(id, body),
    onSuccess: workArea => {
      void queryClient.invalidateQueries({ queryKey: ['workAreas'] });
      setName(workArea.name);
      setVisibility(workArea.visibility);
      setMessage('Work area updated.');
    },
    onError: err => {
      setMessage(err instanceof Error ? err.message : 'Failed to update work area.');
    },
  });

  const addMembershipMutation = useMutation({
    mutationFn: ({ id, codebaseId }: { id: string; codebaseId: string }) =>
      addWorkAreaCodebase(id, { codebaseId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workAreaCodebases', workAreaId] });
      setCodebaseToAdd('');
      setMessage('Codebase added.');
    },
    onError: err => {
      setMessage(err instanceof Error ? err.message : 'Failed to add codebase.');
    },
  });

  const removeMembershipMutation = useMutation({
    mutationFn: ({ id, codebaseId }: { id: string; codebaseId: string }) =>
      removeWorkAreaCodebase(id, codebaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workAreaCodebases', workAreaId] });
      setMessage('Codebase removed.');
    },
    onError: err => {
      setMessage(err instanceof Error ? err.message : 'Failed to remove codebase.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWorkArea(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workAreas'] });
      setSelectedWorkAreaId(null);
    },
    onError: err => {
      setMessage(err instanceof Error ? err.message : 'Failed to delete work area.');
    },
  });

  if (!selectedWorkArea) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border border-border bg-surface-elevated px-4 text-center text-sm text-text-secondary">
        Select a work area to view details.
      </div>
    );
  }

  function updateWorkArea(body: PatchWorkAreaBody): void {
    if (!workAreaId) return;
    setMessage(null);
    patchMutation.mutate({ id: workAreaId, body });
  }

  function handleNameSave(workArea: WorkArea): void {
    const trimmed = name.trim();
    if (!trimmed) {
      setMessage('Name is required.');
      return;
    }
    if (trimmed === workArea.name) return;
    updateWorkArea({ name: trimmed });
  }

  function handleVisibilityChange(nextVisibility: WorkAreaVisibility): void {
    setVisibility(nextVisibility);
    updateWorkArea({ visibility: nextVisibility });
  }

  function handleAddCodebase(): void {
    if (!workAreaId || !codebaseToAdd) return;
    setMessage(null);
    addMembershipMutation.mutate({ id: workAreaId, codebaseId: codebaseToAdd });
  }

  function handleRemoveCodebase(codebaseId: string): void {
    if (!workAreaId) return;
    setMessage(null);
    removeMembershipMutation.mutate({ id: workAreaId, codebaseId });
  }

  const defaultHomeCodebase = selectedWorkArea.defaultHomeCodebaseId
    ? codebaseById.get(selectedWorkArea.defaultHomeCodebaseId)
    : undefined;

  const isTeam = selectedWorkArea.visibility === 'team';

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-surface">
      <div className="border-b border-border px-4 py-3.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold tracking-tight text-text-primary">
                {selectedWorkArea.name}
              </h2>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10.5px]',
                  isTeam
                    ? 'border-[oklch(0.72_0.14_225/0.25)] bg-[oklch(0.72_0.14_225/0.12)] text-accent-bright'
                    : 'border-border bg-surface-elevated text-text-secondary'
                )}
              >
                {isTeam ? <Globe className="size-2.5" /> : <Lock className="size-2.5" />}
                {selectedWorkArea.visibility}
              </span>
            </div>
            {selectedWorkArea.description && (
              <p className="mt-1 text-xs text-text-secondary">{selectedWorkArea.description}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={`/work-areas/${encodeURIComponent(selectedWorkArea.id)}/workbench`}>
                <Table2 className="size-4" />
                Open Workbench
              </Link>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={deleteMutation.isPending}>
                  <Trash2 className="size-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete work area?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes the work area and clears the current selection. This does not
                    delete codebases.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(): void => {
                      deleteMutation.mutate(selectedWorkArea.id);
                    }}
                  >
                    Delete work area
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-auto p-4">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-text-secondary">
              Metadata
            </h3>
            {message && <span className="text-xs text-text-secondary">{message}</span>}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="grid gap-1 text-sm">
              <span className="text-text-secondary">Name</span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={e => {
                    setName(e.target.value);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleNameSave(selectedWorkArea);
                    }
                  }}
                  className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={(): void => {
                    handleNameSave(selectedWorkArea);
                  }}
                  disabled={
                    patchMutation.isPending || !name.trim() || name.trim() === selectedWorkArea.name
                  }
                >
                  Save
                </Button>
              </div>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-text-secondary">Visibility</span>
              <select
                value={visibility}
                onChange={e => {
                  handleVisibilityChange(e.target.value as WorkAreaVisibility);
                }}
                disabled={patchMutation.isPending}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
              >
                <option value="team">team</option>
                <option value="private">private</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-1 gap-3 border-t border-border/70 pt-3 md:grid-cols-2">
            <MetadataRow
              label="Owner"
              value={selectedWorkArea.ownerLogin ?? selectedWorkArea.ownerId ?? 'Unassigned'}
            />
            <MetadataRow
              label="Default codebase"
              value={defaultHomeCodebase ? codebaseLabel(defaultHomeCodebase) : 'None'}
            />
            <MetadataRow
              label="GitHub project"
              value={
                selectedWorkArea.githubProjectOwner && selectedWorkArea.githubProjectNumber
                  ? `${selectedWorkArea.githubProjectOwner} #${String(selectedWorkArea.githubProjectNumber)}`
                  : 'None'
              }
            />
            <MetadataRow label="Created" value={formatDate(selectedWorkArea.createdAt)} />
            <MetadataRow label="Updated" value={formatDate(selectedWorkArea.updatedAt)} />
          </div>
        </section>

        <section className="space-y-3 border-t border-border pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-text-secondary">
              Member codebases
            </h3>
            <div className="flex min-w-0 flex-1 justify-end gap-2">
              <select
                value={codebaseToAdd}
                onChange={e => {
                  setCodebaseToAdd(e.target.value);
                }}
                disabled={addableCodebases.length === 0}
                className="min-w-0 max-w-72 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
              >
                <option value="">Add codebase…</option>
                {addableCodebases.map(codebase => (
                  <option key={codebase.id} value={codebase.id}>
                    {codebaseLabel(codebase)}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                onClick={handleAddCodebase}
                disabled={!codebaseToAdd || addMembershipMutation.isPending}
              >
                Add
              </Button>
            </div>
          </div>

          {loadingMemberships ? (
            <div className="py-4 text-sm text-text-secondary">Loading codebases…</div>
          ) : membershipsError ? (
            <div className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
              Failed to load member codebases.
            </div>
          ) : !memberships || memberships.codebases.length === 0 ? (
            <div className="rounded-md border border-border bg-surface-inset px-3 py-5 text-sm text-text-tertiary">
              No member codebases.
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-border bg-surface-inset">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="w-[60%] px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                      Codebase
                    </th>
                    <th className="w-[28%] px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                      Added
                    </th>
                    <th className="w-[12%] px-3 py-2 text-right font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.codebases.map(membership => {
                    const codebase = codebaseById.get(membership.codebaseId);
                    return (
                      <tr
                        key={membership.codebaseId}
                        className="border-b border-border/60 last:border-0"
                      >
                        <td className="truncate px-3 py-2.5">
                          <span className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-text-primary">
                            <GitBranch className="size-2.5 shrink-0 text-text-tertiary" />
                            {codebase ? codebaseLabel(codebase) : membership.codebaseId}
                          </span>
                        </td>
                        <td className="truncate px-3 py-2.5 font-mono text-[11px] text-text-tertiary">
                          {formatDate(membership.addedAt)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            title="Remove codebase"
                            aria-label="Remove codebase"
                            onClick={(): void => {
                              handleRemoveCodebase(membership.codebaseId);
                            }}
                            disabled={removeMembershipMutation.isPending}
                          >
                            <X className="size-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <WorkAreaEnvVarsPanel
          workAreaId={selectedWorkArea.id}
          memberCodebaseIds={memberCodebaseIdList}
          codebaseById={codebaseById}
        />
      </div>
    </div>
  );
}
